import { Link } from '@tanstack/react-router';
import { PublicShell } from '@/components/layout/PublicShell';
import { useBranding, usePlatformName } from '@/hooks/useBranding';

export function TermsPage() {
  const name = usePlatformName();
  const { data } = useBranding();
  const supportEmail = data?.branding.support_email || 'bot@workercms.com';
  const today = new Date().toLocaleDateString('tr-TR');

  return (
    <PublicShell>
      <article className="container max-w-3xl py-12 space-y-6 text-sm leading-relaxed">
        <header className="space-y-1">
          <h1 className="text-3xl font-bold">Kullanım Şartları</h1>
          <p className="text-muted-foreground">Son güncelleme: {today}</p>
        </header>

        <p>
          Bu Kullanım Şartları ("Şartlar"), {name} ("Platform", "biz") ile sizin ("Kullanıcı")
          aranızdaki yasal sözleşmeyi tanımlar. Platformu kullanarak bu Şartları ve{' '}
          <Link to="/privacy" className="text-primary hover:underline">Gizlilik Politikası</Link>
          {' '}ile{' '}
          <Link to="/aup" className="text-primary hover:underline">Kabul Edilebilir Kullanım Politikası</Link>'nı
          kabul etmiş olursunuz.
        </p>

        <Section title="1. Hizmet Tanımı">
          <p>
            {name}, AI tabanlı içerik üretimi ve WordPress sitelerine otomatik yayınlama sağlayan
            bir SaaS platformudur. Platform; içerik üretimi, görsel oluşturma, zamanlanmış yayınlama,
            toplu üretim ve benzeri özellikleri kredi bazlı ücretlendirme modeliyle sunar.
          </p>
        </Section>

        <Section title="2. Hesap ve Sorumluluk">
          <ul className="list-disc pl-5 space-y-1">
            <li>18 yaşından büyük olmanız veya ebeveyn onayına sahip olmanız gerekir.</li>
            <li>Hesap bilgilerinizin güvenliğinden siz sorumlusunuz.</li>
            <li>Hesabınızdan yapılan tüm işlemler size aittir.</li>
            <li>Yetkisiz erişim fark ederseniz derhal bize bildirmelisiniz.</li>
            <li>Kayıt sırasında verdiğiniz bilgilerin doğru ve güncel olmasından siz sorumlusunuz.</li>
          </ul>
        </Section>

        <Section title="3. Ücretlendirme ve Ödeme">
          <ul className="list-disc pl-5 space-y-1">
            <li>Platform kredi bazlı çalışır. Bakiyenizden her kullanım kadar düşülür.</li>
            <li>Kredi bakiyesi, kripto para (USDT, USDC vb.) ile ödeme yapılarak yüklenir.</li>
            <li>Ödeme onaylandıktan sonra bakiye hesabınıza eklenir; bloğun kesinleşmesi bekleme süresi gerektirebilir.</li>
            <li>Eklentiler (API erişimi, BYOK, Uzun Makale, Web Grounding vb.) ayrı ücretlendirilir.</li>
            <li>Platform, fiyatları ve kredi maliyetlerini önceden bildirerek güncelleme hakkını saklı tutar.</li>
          </ul>
        </Section>

        <Section title="4. İade Politikası">
          <p>
            Kripto ödemeler geri dönüşsüzdür; <strong>bu nedenle yüklenen bakiye iade edilmez</strong>.
            Ancak Platform kaynaklı teknik hata nedeniyle kredi tüketilmiş ise, destek talebinizle
            krediniz hesabınıza iade edilir. Tüketilmiş kredi, siz hatalı kullandığınız için iade
            kapsamında değildir.
          </p>
        </Section>

        <Section title="5. Fikri Mülkiyet">
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <strong>Üretilen içerik:</strong> Prompt ile üretilen metin ve görsellerin kullanım hakkı size aittir.
              Sağlayıcıların kendi hizmet şartlarına tabidir.
            </li>
            <li>
              <strong>Platform:</strong> {name} markası, yazılımı ve arayüzü Platform sahibine aittir.
              Tersine mühendislik, kopyalama veya yeniden dağıtım yasaktır.
            </li>
            <li>
              <strong>Kullanıcı içeriği:</strong> Yüklediğiniz WordPress verileri ve promptlar sizindir;
              Platform bunları yalnızca hizmeti sunmak için işler.
            </li>
          </ul>
        </Section>

        <Section title="6. Yapay Zeka Çıktıları ve Doğruluk">
          <p>
            Yapay zeka modelleri hatalı, eksik veya yanıltıcı çıktılar üretebilir. Üretilen içeriğin
            doğruluğunu kontrol etmek, olgu doğrulaması yapmak ve yayın öncesi gözden geçirmek sizin
            sorumluluğunuzdadır. Platform, AI çıktılarının kullanılmasından doğacak sonuçlardan sorumlu
            değildir.
          </p>
        </Section>

        <Section title="7. Üçüncü Taraf Hizmetler">
          <p>
            Platform, içerik üretimi için AI sağlayıcılarına, görsel depolama için Cloudflare R2'ye,
            yayın için bağladığınız WordPress sitelerine bağımlıdır. Bu hizmetlerin kesintileri
            Platform hizmetini etkileyebilir; bu durumlarda Platform sorumluluk kabul etmez.
          </p>
        </Section>

        <Section title="8. Sorumluluk Sınırlaması">
          <p>
            Platform hizmet "olduğu gibi" sunulur; kesintisizlik, hatasızlık veya belirli bir amaca
            uygunluk garanti edilmez. Yasaların izin verdiği azami ölçüde, {name}; doğrudan, dolaylı,
            arızi veya sonuçsal hiçbir zarardan sorumlu değildir. Toplam sorumluluğumuz, olayın
            meydana geldiği aydan önceki 3 ay içinde ödediğiniz ücretle sınırlıdır.
          </p>
        </Section>

        <Section title="9. Fesih">
          <ul className="list-disc pl-5 space-y-1">
            <li>Hesabınızı dilediğiniz zaman hesap ayarlarından kapatabilirsiniz.</li>
            <li>
              Şartlara veya{' '}
              <Link to="/aup" className="text-primary hover:underline">AUP</Link>
              'ye aykırı kullanım halinde hesabınızı önceden bildirim yapmaksızın askıya alabilir veya kapatabiliriz.
            </li>
            <li>Hesap kapandığında, kullanılmamış bakiye iade edilmez; veriler 90 gün içinde silinir.</li>
          </ul>
        </Section>

        <Section title="10. Değişiklikler">
          <p>
            Bu Şartlarda değişiklik yapıldığında, değişiklik yürürlüğe girmeden önce hesabınıza kayıtlı
            e-posta adresinize bildirim gönderilir. Değişikliklerden sonra Platformu kullanmaya devam
            etmeniz, güncel Şartları kabul ettiğiniz anlamına gelir.
          </p>
        </Section>

        <Section title="11. Geçerli Hukuk ve Uyuşmazlıklar">
          <p>
            Bu Şartlar Türkiye Cumhuriyeti kanunlarına tabidir. Uyuşmazlıklar İstanbul Merkez
            mahkemelerinde çözülecektir. Tüketici haklarınız saklıdır.
          </p>
        </Section>

        <Section title="12. İletişim">
          <p>
            Sorularınız için:{' '}
            <a className="text-primary hover:underline" href={`mailto:${supportEmail}`}>
              {supportEmail}
            </a>
          </p>
        </Section>
      </article>
    </PublicShell>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h2 className="text-xl font-semibold">{title}</h2>
      {children}
    </section>
  );
}
